import React, { useState, useEffect, useContext, useRef } from 'react';
import LayoutSelectButton from 'components/layout/LayoutSelectButton.jsx';
import LayoutCarousel from 'components/layout/LayoutCarousel.jsx';
import LayoutMostEfficient from 'components/layout/LayoutMostEfficient.jsx';
import LayoutNavSubSection from 'components/layout/LayoutNavSubSection.jsx';
import PerSectionDownloadButtons from 'components/layout/PerSectionDownloadButtons.jsx';
import LayoutNestCombinations from 'components/layout/LayoutNestCombinations.jsx';
import LayoutTabRow from 'components/layout/LayoutTabRow.jsx';
import ChooseLayoutParts from 'components/layout/ChooseLayoutParts.jsx';
import objectifyForm from 'utils/objectifyForm';

// A nest's tab leaves out the words all of the section's part names start with ("Hard cardboard of the"), which its
// heading already says: a tab is at most 260px wide (custom_style.scss), and whole part names run to many lines in
// one. Every part keeps a word. Only until Claude's labels are stored (navSection.tab_labels,
// LayoutPartNestingTabLabels).
export const nestTabLabel = (partNames, sectionPartNames) => {
  const words = sectionPartNames.map((name) => name.trim().split(/\s+/));
  let common = 0;
  while (words.every((nameWords) => nameWords.length > common + 1 && nameWords[common] === words[0][common])) { common += 1; }
  const label = partNames.map((name) => name.trim().split(/\s+/).slice(common).join(' ')).join(' & ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// The "Choose parts" tab is no sub_section, so the tabs selected on mount are selected whichever tab a section opens on.
const CHOOSE_LAYOUT_PARTS_TAB = 'choose_layout_parts';

const sameParts = (parts, otherParts) => parts.length === otherParts.length && parts.every((part) => otherParts.includes(part));

const sameMembers = (uuids, otherUuids) => uuids.length === otherUuids.length && uuids.every((uuid) => otherUuids.includes(uuid));

const holdsTemplates = (navSection) => navSection.sub_sections.some((sub_section) => sub_section.members.some((member) => !member.dynamic_nesting));

// The tabs the server found to hold every part on the fewest sheets (LayoutNavSectionsMostEfficientDecorator), the
// ranked nests' best combination, or a tab of chosen parts; else the first tab.
const selectsOnMount = (subSections, index) => (
  subSections.some((sub_section) => sub_section.select_on_mount) ? Boolean(subSections[index].select_on_mount) : index == 0
);

const openingTab = (navSection) => Math.max(0, navSection.sub_sections.findIndex((sub_section) => sub_section.select_on_mount));

const LayoutNavSection = (props) => {
  const [activeTab, setActiveTab] = useState(() => openingTab(props.navSection));
  const [items, setItems] = useState([]);
  const [activeMember, setActiveMember] = useState(props.navSection.first_member);
  const [error, setError] = useState("error");
  // A section of parts no template holds (LayoutNavSectionsDynamicNestingDecorator) opens on their nest of every part,
  // then gets a tab for each nest of the combinations ranked for it: the best combination's nests first, selected and
  // flagged, every tab on the sheet the ranking was made on. The others are picked as templates are. The parts the
  // viewer chooses add tabs at the end (#nestChosenParts).
  const [subSections, setSubSections] = useState(props.navSection.sub_sections);
  const [ranked, setRanked] = useState(false);
  const partSetMembers = props.navSection.part_set_members || [];
  const tabChosenFrom = useRef(0);
  const chosenTabAdded = useRef(false);
  const [memberToSelect, setMemberToSelect] = useState(null);

  // A section of templates ranked with its nests too (LayoutNavSectionsDynamicNestingDecorator) is ranked once the first
  // tab it selects is drawn, and then selects the best combination in place of the templates it selected -- unless the
  // viewer changed the selection or a form meanwhile.
  const ranksTemplates = Boolean(props.navSection.combinations_url) && holdsTemplates(props.navSection);
  const firstSelectedTab = props.navSection.sub_sections.find((sub_section) => sub_section.select_on_mount);
  const firstDrawn = firstSelectedTab && firstSelectedTab.members.find((member) => member.uuid === firstSelectedTab.selected_member_uuid);
  const viewerChanged = useRef(false);
  // A field of a tab's form; the drawing's own controls (its background) change no layout.
  const markViewerChanged = (event) => {
    if (event.target.closest && event.target.closest('form')) { viewerChanged.current = true; }
  };

  const sectionBusy = () => subSections.some((sub_section) => sub_section.members.some((member) => props.activeRedraws.includes(member.uuid)));

  const nestLabel = (member) => (props.navSection.tab_labels || {})[member.uuid] || nestTabLabel(member.part_names, props.navSection.part_names);

  // A ranking that lands after the viewer added a tab of their own (a first one failed, and a later redraw asked again)
  // would replace that tab and drop what is selected.
  const showRankedNests = (combinations, layout) => {
    if (chosenTabAdded.current) { return; }

    const nests = [];
    combinations.forEach((combination) => combination.members.forEach((member) => {
      if (!nests.some((nest) => nest.uuid === member.uuid)) { nests.push(member); }
    }));
    if (nests.length < 2) { return; }

    const bestUuids = combinations[0].members.map((member) => member.uuid);

    setItems([]);
    setActiveTab((current) => (current === CHOOSE_LAYOUT_PARTS_TAB ? current : 0));
    tabChosenFrom.current = 0;
    setMemberToSelect(null);
    setRanked(true);
    setSubSections(nests.map((member) => ({
      label: nestLabel(member),
      parts: member.parts,
      part_names: member.part_names,
      members: [{ ...member, initial_layout: layout }],
      most_efficient_member_uuids: bestUuids.includes(member.uuid) ? [{ uuid: member.uuid }] : [],
      select_on_mount: bestUuids.includes(member.uuid),
    })));
  }

  // Each member of the best combination in the tab holding it, a nest no tab holds in a tab of its own; the other tabs
  // select nothing. A nest is drawn on the sheet the ranking was made on. A combination tied with the best that is the
  // selection already is kept.
  const selectRankedCombination = (combinations, layout) => {
    if (viewerChanged.current || chosenTabAdded.current || combinations.length === 0) { return; }

    const selectedOnLoad = subSections.filter((sub_section) => sub_section.select_on_mount).map((sub_section) => sub_section.selected_member_uuid);
    const best = combinations[0].members;
    const tiedWithBest = combinations.filter((combination) => combination.sheets_per_thousand_sets === combinations[0].sheets_per_thousand_sets);
    if (!sameMembers(items.map((item) => item.uuid), selectedOnLoad)) { return; }
    if (tiedWithBest.some((combination) => sameMembers(combination.members.map((member) => member.uuid), selectedOnLoad))) { return; }

    const tabIndexOf = (member) => subSections.findIndex((sub_section) => sub_section.members.some((candidate) => candidate.uuid === member.uuid));
    const untabbed = best.filter((member) => tabIndexOf(member) < 0);
    if (untabbed.some((member) => !member.dynamic_nesting)) { return; }

    const tabs = subSections.map((sub_section, index) => {
      const member = best.find((candidate) => tabIndexOf(candidate) === index);
      return {
        ...sub_section,
        members: sub_section.members.map((candidate) => (member && candidate.uuid === member.uuid && candidate.dynamic_nesting ? { ...candidate, initial_layout: layout } : candidate)),
        select_on_mount: Boolean(member),
        selected_member_uuid: member ? member.uuid : undefined,
      };
    });
    const addedTabs = untabbed.map((member) => {
      const nest = { ...(partSetMembers.find((candidate) => candidate.uuid === member.uuid) || member), initial_layout: layout };
      return { label: nestLabel(nest), parts: nest.parts, part_names: nest.part_names, members: [nest], most_efficient_member_uuids: [],
        select_on_mount: true, selected_member_uuid: nest.uuid };
    });

    const nextSubSections = tabs.concat(addedTabs);
    setItems([]);
    setMemberToSelect(null);
    setRanked(true);
    setSubSections(nextSubSections);
    setActiveTab((current) => (current === CHOOSE_LAYOUT_PARTS_TAB || (nextSubSections[current] && nextSubSections[current].select_on_mount)
      ? current : openingTab({ sub_sections: nextSubSections })));
  };

  // As the drawn member's form posts it; none when nothing is drawn there.
  const sheetOfTabChosenFrom = () => {
    const tab = subSections[tabChosenFrom.current];
    const form = tab && tab.members.map((member) => $(`#form_${member.uuid}`)).find((candidate) => candidate.length > 0);
    return form ? objectifyForm(form) : undefined;
  };

  const openChooseLayoutPartsTab = () => {
    if (activeTab !== CHOOSE_LAYOUT_PARTS_TAB) { tabChosenFrom.current = activeTab; }
    setActiveTab(CHOOSE_LAYOUT_PARTS_TAB);
  };

  // A nest is in one tab only: its uuid names its form, its drawing and the events of its redraw. So the tab already
  // holding the parts selects their nest there.
  const nestChosenParts = (parts) => {
    const member = partSetMembers.find((candidate) => sameParts(candidate.parts, parts));
    if (member == null) { return; }

    const nest = { ...member, initial_layout: sheetOfTabChosenFrom() };
    const tabIndex = subSections.findIndex((sub_section) => sameParts(sub_section.parts, parts));
    if (tabIndex >= 0) {
      if (subSections[tabIndex].members.some((candidate) => candidate.uuid === member.uuid)) { setMemberToSelect({ tabIndex, member: nest }); }
      setActiveTab(tabIndex);
      return;
    }

    const addedTabIndex = subSections.length;
    chosenTabAdded.current = true;
    setSubSections(subSections.concat([{
      label: nestLabel(member),
      parts: member.parts,
      part_names: member.part_names,
      members: [nest],
      most_efficient_member_uuids: [],
      select_on_mount: true,
    }]));
    setActiveTab(addedTabIndex);
  };

  const selectedItemCount = (sub_section) => {
    let item_uuids = items.map((i) => i.uuid );
    let sub_section_uuids = sub_section.members.map((m) => m.uuid);
    let intersection = item_uuids.filter(value => sub_section_uuids.includes(value));
    return intersection.length;
  }

  const requiredPartsMissingError = () => {
    let item_parts = items.map((item) => item.part_names).flat();
    let missingParts = props.navSection.part_names.filter(value => !item_parts.includes(value));

    if (missingParts.length > 0) {
      return `${window.wording_select_templates_for_parts.replace('(?)',missingParts.map(str => `"${str}"`).join(', ')) }`;
    }

    return '';
  }

  const isMostEfficient = (member_uuid) => {
    for(let key in mostEfficients) {
      for(let i=0; i< mostEfficients[key].length; i++) {
        let member = mostEfficients[key][i];
        if(member.uuid == member_uuid) {
          return true;
        }
      }
    }
    return false;
  }

  useEffect(() => {
    let item_part_names = items.map((item) => item.part_names).flat();
    props.setSelectedMembers(items, `index_${props.index}`);
  }, [items])

  useEffect(() => {
    props.setRequiredParts(props.navSection.part_names);
  }, [])

  const onItemsChanged = () => {
    if(items.length == 0) { return };
    let lastItem = items[items.length - 1];
    for(let i = 0; i< items.length -1; i++){
      let intersection = items[i].parts.filter(x => lastItem.parts.includes(x));
      if( intersection.length > 0 ){
        setItems((prev) => prev.filter((item) => item.uuid != items[i].uuid))
      }
    }
  }

  useEffect(onItemsChanged, [items]);

  const choosesParts = partSetMembers.length > 0;
  const tabCount = subSections.length + (choosesParts ? 1 : 0);

  const tabs = [
    ...(choosesParts ? [{ index: CHOOSE_LAYOUT_PARTS_TAB, icon: 'fa-plus', label: t('layouts.choose_layout_parts_tab'), selectedCount: 0 }] : []),
    ...subSections.map((sub_section, index) => ({ index, label: sub_section.label, selectedCount: selectedItemCount(sub_section) })),
  ];

  const openTab = (index) => {
    if (index === CHOOSE_LAYOUT_PARTS_TAB) {
      openChooseLayoutPartsTab();
    } else {
      setActiveTab(index);
    }
  };

  const getSubSection = (sub_section, index) => {
    return <LayoutNavSubSection
      key={`sub_section_${props.index}_${index}_${ranked}`}
      sub_section={sub_section}
      index={index}
      selectOnMount={selectsOnMount(subSections, index)}
      memberToSelect={memberToSelect && memberToSelect.tabIndex === index ? memberToSelect.member : null}
      activeTab={activeTab}
      activeRedraws={props.activeRedraws}
      setActiveRedraws={props.setActiveRedraws}
      items={items}
      setItems={setItems}
      requiredPartsMissingError={requiredPartsMissingError}
      ranksTemplates={ranksTemplates}
    />
  }

  return (
    <div className={`col-lg-12 navSection ${props.navSection.type == 'two_layers' ? 'card navSectionCardModif' : ''}`}
      onChangeCapture={markViewerChanged} onInputCapture={markViewerChanged}>
      {props.navSection.type == 'two_layers' && (
      <div className="promo-block">
        <div className="promo-text"> {props.navSection.label} </div>
        <div className="center-line"></div>
      </div>
      )}

      {props.navSection.combinations_url && (!ranksTemplates || firstDrawn) && (
        <LayoutNestCombinations
          combinationsUrl={props.navSection.combinations_url}
          member={ranksTemplates ? firstDrawn : props.navSection.sub_sections[0].members[0]}
          busy={sectionBusy()}
          onRanked={ranksTemplates ? selectRankedCombination : showRankedNests}
        />
      )}

      {(props.navSection.type == 'two_layers' || props.navSection.type == 'one_layer') && tabCount > 1 && (
        <LayoutTabRow tabs={tabs} activeTab={activeTab} onOpen={openTab} disabled={props.activeRedraws.length > 0} />
      )}

      <div className={`col-lg-12 newtabcontent navSectionContent ${props.navSection.type == 'two_layers' ? '' : 'navSectionContentWider'} `}>
        {choosesParts && (
          <ChooseLayoutParts
            partMembers={partSetMembers.filter((member) => member.parts.length === 1)}
            labelOf={nestLabel}
            visible={activeTab === CHOOSE_LAYOUT_PARTS_TAB}
            disabled={props.activeRedraws.length > 0}
            onNest={nestChosenParts}
          />
        )}
        {subSections.map(getSubSection)}
      </div>

      {props.comboGuestPresent && (
        <div className="col-lg-12 navSectionDownloads">
          <PerSectionDownloadButtons
            items={items}
            activeRedraws={props.activeRedraws}
          />
        </div>
      )}
   </div>
  )
}

export default LayoutNavSection;
